import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { isGoneForGood } from '@/features/entity-resolution/merged-redirect'
import { edgeFromRelationship, makeMeta, nodeFromPerson } from '@/features/graph/utils/builders'
import { api } from '@/services/api'
import { graphApi } from '@/services/api/graph'
import { getOrCreateInvestigationsStore } from '@/stores/investigations-store'
import { emptyViewState } from '@/types/graph-view'
import {
  afterEvent,
  type IdentityReadEvent,
  type IdentityRef,
  sameIdentity,
  unread,
} from '../identity-read'
import type { IdentityWithDocuments } from '../types'
import { type NamedSurvivor, survivorNamedBy } from './useMergedInto'

export interface UseIdentityDetailResult {
  data: IdentityWithDocuments | null
  isLoading: boolean
  error: string | null
  /**
   * Where the read said the identity went: a merge folded it into another one,
   * and the read named the survivor (ADR-0053). The page follows it there.
   */
  mergedInto: NamedSurvivor | null
  openingGraph: boolean
  updateField: (field: string, next: string) => Promise<void>
  openInGraphView: () => Promise<void>
  /**
   * Re-read the identity in place — the faces card calls it after the main
   * photo moves. Held by a callback that outlived the page it was made on, it
   * does nothing.
   */
  refresh: () => Promise<void>
}

/**
 * Page-level data + actions for the identity detail view. Owns ALL imperative
 * / IO concerns — REST (`api.identities.*`), graph fetch (`graphApi`), the
 * per-case investigations store, the document overlay, and navigation — so the
 * page itself can stay a thin presentational orchestrator.
 *
 * Everything it holds is one identity's (`identity-read.ts`): when the page
 * moves to another identity it starts over, and nothing asked about the
 * identity before lands on it.
 */
export function useIdentityDetail({ caseId, identityId }: IdentityRef): UseIdentityDetailResult {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [stored, setStored] = useState(() => unread({ caseId, identityId }))
  // Another identity is another read: nothing of the one before is shown, not
  // even for the render before its own read begins.
  const onShow = { caseId, identityId }
  const read = sameIdentity(stored, onShow) ? stored : unread(onShow)
  if (read !== stored) setStored(read)

  // What a request of this render learns is about this render's identity,
  // whenever it arrives.
  const land = useCallback(
    (event: IdentityReadEvent) =>
      setStored(current => afterEvent(current, { caseId, identityId }, event)),
    [caseId, identityId]
  )

  // The identity on show, for whatever outlives the render it was made in — an
  // upload's extraction runs for minutes. Null once the page is gone.
  const shown = useRef<IdentityRef | null>(null)
  useEffect(() => {
    shown.current = { caseId, identityId }
    return () => {
      shown.current = null
    }
  }, [caseId, identityId])
  const stillShown = useCallback(
    () => shown.current !== null && sameIdentity(shown.current, { caseId, identityId }),
    [caseId, identityId]
  )

  const fetchIdentity = useCallback(async () => {
    land({ type: 'started' })
    try {
      // identityId comes URL-encoded from params, decode it first
      const decodedIdentityId = decodeURIComponent(identityId)
      const result = await api.identities.get(caseId, decodedIdentityId)
      land({ type: 'loaded', data: result as unknown as IdentityWithDocuments })
    } catch (err: unknown) {
      console.error('Fetch error:', err)
      land({ type: 'failed', error: err, at: Date.now() })
    }
  }, [caseId, identityId, land])

  useEffect(() => {
    fetchIdentity()
  }, [fetchIdentity])

  // An identity the page no longer shows is not read again: the answer would be
  // dropped, and the read would stay on the audit record (`subject.view`) as a
  // view that nobody had.
  const refresh = useCallback(async () => {
    if (stillShown()) await fetchIdentity()
  }, [fetchIdentity, stillShown])

  // Inline-edit save handler — PATCH /v1/identities/{id} and refresh detail.
  const updateField = useCallback(
    async (field: string, next: string) => {
      try {
        const updated = (await api.identities.update(caseId, identityId, {
          [field]: next,
        })) as unknown as IdentityWithDocuments
        land({ type: 'saved', data: updated })
      } catch (err: unknown) {
        // Nothing reads the identity after a save that found it gone, so read it
        // now: a merged one names its survivor, and the page moves there.
        if (isGoneForGood(err)) void refresh()
        throw err
      }
    },
    [caseId, identityId, land, refresh]
  )

  // Open in graph view — fetch 2-hop neighbourhood, push to per-case
  // investigations store as a transient view, then navigate to /graph.
  const { data, openingGraph } = read
  const openInGraphView = useCallback(async () => {
    if (openingGraph || !data) return
    land({ type: 'graph', opening: true })
    try {
      const res = (await graphApi.getNodeNeighbors(caseId, identityId, { hops: 2 })) as {
        nodes: Parameters<typeof nodeFromPerson>[0][]
        relationships: Parameters<typeof edgeFromRelationship>[0][]
      }
      // The page moved on while the neighbourhood was read: nobody is waiting
      // for this identity's graph any more.
      if (!stillShown()) return
      const nodes = res.nodes.map(nodeFromPerson)
      // The neighbors endpoint excludes the starting node (it assumes the caller
      // has it). Add it back so edges from this person have a valid source.
      if (!nodes.some(n => n.id === identityId)) {
        nodes.unshift({
          id: identityId,
          name: data.identity.name,
          labels: ['Person'],
          typeLabel: 'Person',
          properties: data.identity.properties ?? {},
        })
      }
      const view = {
        meta: makeMeta(caseId, data.identity.name),
        nodes,
        edges: res.relationships.map(edgeFromRelationship),
        view: emptyViewState(),
      }
      getOrCreateInvestigationsStore({ caseId }).getState().setTransient(view, data.identity.name)
      navigate(`/cases/${caseId}/graph`)
    } finally {
      land({ type: 'graph', opening: false })
    }
  }, [caseId, data, identityId, land, navigate, openingGraph, stillShown])

  const failure = isGoneForGood(read.error)
    ? 'identity.detail.notFound'
    : 'identity.detail.errors.load'
  return {
    data,
    isLoading: read.isLoading,
    error: read.error ? t(failure) : null,
    mergedInto: survivorNamedBy(read),
    openingGraph,
    updateField,
    openInGraphView,
    refresh,
  }
}
